import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { usersApi } from '../api';
import type { User } from '../types';
import { toOptionLabel } from '@/features/lookups/snapshot';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronsUpDown, Check, Loader2, Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { authApi } from '@/features/auth/api';
import { useAuth } from '@/hooks/useAuth';

const schema = z.object({
  full_name: z.string().min(1, 'Name is required').max(120),
  email: z.string().email('Invalid email'),
  role_id: z.number().int().positive({ message: 'Role is required' }),
  is_active: z.preprocess((v) => (v === null || v === undefined ? false : v), z.boolean()),
  phone: z.string().optional(),
  birth_date: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function UserFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdminWithShelter, shelterId: authShelterId } = useAuth();

  const { register, control, handleSubmit, reset, formState: { errors, dirtyFields }, setValue, watch, setError, clearErrors } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { is_active: true, phone: '', birth_date: '' },
    mode: 'onSubmit',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const emailValue = watch('email') || '';

  const { data: rolesData } = useQuery({
    queryKey: ['roles-list'],
    queryFn: () => usersApi.listRoles(),
  });
  const rolesRows: any[] = Array.isArray(rolesData) ? (rolesData as any[]) : (rolesData as any)?.data || [];
  const roleOpts = rolesRows.map((r: any) => ({ id: r.id, label: toOptionLabel(r) }));
  const getRoleName = (r: any) => (r?.name || r?.label || r?.slug || '').toString().toLowerCase();
  const userRole = rolesRows.find((r: any) => getRoleName(r) === 'user');
  const userRoleId = userRole ? Number(userRole.id) : undefined;
  const selectedRoleId = watch('role_id');
  const selectedRole = rolesRows.find((r: any) => Number(r.id) === Number(selectedRoleId));
  const roleName = getRoleName(selectedRole);
  const isShelterRequired = roleName === 'user';
  const canSelectShelter = !isAdminWithShelter && (roleName === 'user' || roleName === 'admin');

  useEffect(() => {
    if (!isAdminWithShelter) return;
    if (isEdit) return;
    if (!userRoleId) return;
    setValue('role_id', userRoleId, { shouldDirty: true, shouldValidate: true });
    clearErrors('role_id');
  }, [isAdminWithShelter, isEdit, userRoleId, setValue, clearErrors]);

  useEffect(() => {
    if (!isAdminWithShelter) return;
    if (!authShelterId) return;
    setSelectedShelterId(Number(authShelterId));
  }, [isAdminWithShelter, authShelterId]);

  // Shelters options (id + name)
  const { data: sheltersData } = useQuery({
    queryKey: ['shelters-simple', canSelectShelter],
    queryFn: () => usersApi.listSheltersSimple(),
    enabled: canSelectShelter,
  });
  const shelterRows: any[] = Array.isArray(sheltersData) ? (sheltersData as any[]) : (sheltersData as any)?.data || [];
  const shelterOpts = shelterRows.map((s: any) => ({ id: s.id, label: s.name }));
  // If there is only one shelter available, auto-select it for convenience
  useEffect(() => {
    if (!canSelectShelter) return;
    if ((shelterOpts || []).length === 1 && !selectedShelterId) {
      setSelectedShelterId(Number(shelterOpts[0].id));
      setSelectedShelterName(String(shelterOpts[0].label || ''));
    }
  }, [shelterOpts, canSelectShelter]);

  // User-shelter single association state
  const [selectedShelterId, setSelectedShelterId] = useState<number | undefined>();
  const [selectedShelterName, setSelectedShelterName] = useState<string>('');
  const [roleInShelter, setRoleInShelter] = useState<string>('');
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const { data: existing } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.getById(Number(id)),
    enabled: isEdit,
  });

  const initialRef = useRef<any | null>(null);
  const initialEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (existing) {
      const mapped: FormData = {
        full_name: (existing as any).full_name || (existing as any).fullName || '',
        email: (existing as any).email || '',
        role_id: Number((existing as any).role_id ?? (existing as any).roleId) || (undefined as any),
        is_active: Boolean((existing as any).is_active ?? (existing as any).isActive),
        phone: (existing as any).phone,
        birth_date: ((existing as any).birth_date ?? (existing as any).birthDate)?.slice(0,10),
      } as any;
      reset(mapped);
      initialRef.current = mapped;
      initialEmailRef.current = mapped.email;
    }
  }, [existing, reset]);

  useEffect(() => {
    const value = emailValue.trim();
    const sameAsInitial = isEdit && initialEmailRef.current === value;
    if (!value || sameAsInitial || !/^[^@]+@[^@]+\.[^@]+$/.test(value)) {
      clearErrors('email');
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setIsCheckingEmail(true);
        const exists = await authApi.checkEmailExists(value);
        if (cancelled) return;
        if (exists) {
          setError('email', { type: 'manual', message: 'El correo ya está registrado.' });
        } else {
          clearErrors('email');
        }
      } catch {
        if (!cancelled) clearErrors('email');
      } finally {
        if (!cancelled) setIsCheckingEmail(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [emailValue, isEdit, setError, clearErrors]);

  // Load user shelters if editing
  const { data: existingLinks } = useQuery({
    queryKey: ['user-shelters', id],
    queryFn: () => usersApi.listUserShelters(Number(id)),
    enabled: isEdit,
  });
  useEffect(() => {
    if (existingLinks && Array.isArray(existingLinks) && existingLinks.length > 0) {
      const ln = existingLinks[0];
      setSelectedShelterId(Number(ln.shelterId));
      setSelectedShelterName(ln.shelterName);
      setRoleInShelter(ln.roleInShelter || '');
    }
  }, [existingLinks]);

  // When role changes to one that can't select shelter, clear selection to avoid confusion
  useEffect(() => {
    if (!canSelectShelter) {
      setSelectedShelterId(undefined);
      setSelectedShelterName('');
      setRoleInShelter('');
    }
  }, [canSelectShelter]);

  function Combobox({ value, onChange, options, placeholder }: { value?: number; onChange: (v?: number)=>void; options: {id:number;label:string}[]; placeholder?: string; }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const current = options.find(o=>o.id===value);
    const filtered = options.filter(o=>o.label.toLowerCase().includes(query.toLowerCase())).slice(0,100);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
            {current ? current.label : (placeholder || 'Selecciona...')}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Buscar..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filtered.map(o=> (
                  <CommandItem key={o.id} value={o.label} onSelect={()=>{ onChange(o.id); setOpen(false); }}>
                    <Check className={"mr-2 h-4 w-4 "+(o.id===value?'opacity-100':'opacity-0')} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // Phone formatting: 000 000 0000
  const formatPhone = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 10);
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);
    return [part1, part2, part3].filter(Boolean).join(' ');
  };

  const onSubmit = async (data: FormData) => {
    if (isAdminWithShelter && !userRoleId) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se encontró el rol "user".' });
      return;
    }
    if (isAdminWithShelter && !authShelterId) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se encontró la entidad asociada al admin.' });
      return;
    }
    const resolvedRoleId = isAdminWithShelter && userRoleId ? userRoleId : data.role_id;
    const resolvedShelterId = isAdminWithShelter ? Number(authShelterId) : selectedShelterId;
    if (isShelterRequired && !resolvedShelterId) {
      toast({ variant: 'destructive', title: 'Validation', description: 'Entidad es obligatoria para el rol "user"' });
      return;
    }
    const emailTrimmed = (data.email || '').trim();
    if (!isEdit || emailTrimmed !== (initialEmailRef.current || '').trim()) {
      try {
        setIsCheckingEmail(true);
        const exists = await authApi.checkEmailExists(emailTrimmed);
        if (exists) {
          setError('email', { type: 'manual', message: 'El correo ya está registrado.' });
          setIsCheckingEmail(false);
          return;
        }
        setIsCheckingEmail(false);
      } catch {
        // ignore and proceed
      } finally {
        setIsCheckingEmail(false);
      }
    }
    const digitsPhone = String(data.phone || '').replace(/\D/g, '');
    const fullPayload: any = {
      fullName: data.full_name,
      email: data.email,
      roleId: resolvedRoleId,
      isActive: data.is_active,
      ...(digitsPhone ? { phone: digitsPhone } : {}),
    };
    if (data.birth_date && data.birth_date.trim() !== '') {
      fullPayload.birthDate = data.birth_date;
    }

    try {
      setIsSaving(true);
      let saved: any;
      if (isEdit) {
        const partial: any = {};
        Object.keys(dirtyFields || {}).forEach((k) => {
          switch (k) {
            case 'full_name': partial.fullName = data.full_name; break;
            case 'email': partial.email = data.email; break;
            case 'role_id': partial.roleId = resolvedRoleId; break;
            case 'is_active': partial.isActive = data.is_active; break;
            case 'phone': {
              const d = String(data.phone || '').replace(/\D/g, '');
              partial.phone = d; // allow clearing to empty string by leaving dirty phone as ''
              break;
            }
            case 'birth_date':
              partial.birthDate = (data.birth_date && data.birth_date.trim() !== '') ? data.birth_date : null;
              break;
            // password changes are not allowed on this screen
            case 'password': break;
            default: break;
          }
        });
        const payloadToSend = Object.keys(partial).length ? partial : fullPayload;
        saved = await usersApi.update(Number(id), payloadToSend);
      } else {
        saved = await usersApi.create(fullPayload);
      }
      const userId = Number(isEdit ? id : saved.id);
      // Single association replace (0 or 1)
      const linksPayload = resolvedShelterId
        ? [{ shelterId: Number(resolvedShelterId), roleInShelter: roleInShelter || undefined }]
        : [];
      await usersApi.putUserShelters(userId, linksPayload);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: isEdit ? 'Usuario actualizado' : 'Usuario creado',
        description: isEdit
          ? undefined
          : 'Se envió un correo con un código OTP para que el usuario configure su contraseña.',
      });
      navigate('/users');
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'Failed to save user' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold">{isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}</h1>
            <p className="text-muted-foreground">{isEdit ? 'Actualiza la información del usuario' : 'Crea un nuevo usuario'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Card className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="full_name">Nombre *</Label>
                <Input id="full_name" {...register('full_name')} placeholder="Nombre completo" />
                {errors.full_name && <p className="text-sm text-destructive">{errors.full_name.message}</p>}
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" {...register('email')} placeholder="user@email.com" />
                {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                {isCheckingEmail && <p className="text-xs text-muted-foreground">Verificando email...</p>}
              </div>
              {!isAdminWithShelter && (
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Controller
                    name="role_id"
                    control={control}
                    render={({ field }) => (
                      <Combobox value={field.value} onChange={field.onChange} options={roleOpts} placeholder="Selecciona un rol" />
                    )}
                  />
                  {errors.role_id && <p className="text-sm text-destructive">{errors.role_id.message}</p>}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="phone"
                      inputMode="numeric"
                      placeholder="000 000 0000"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(formatPhone(e.target.value))}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="birth_date">Fecha de nacimiento</Label>
                <Input id="birth_date" type="date" {...register('birth_date')} />
              </div>
              {isAdminWithShelter && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Función en la entidad</Label>
                  <Input value={roleInShelter} onChange={(e)=>setRoleInShelter(e.target.value)} placeholder="voluntario, gestor, etc." />
                </div>
              )}
              {/* La contraseña no se edita/ingresa aquí: el sistema envía OTP por correo */}
              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4 md:col-span-2">
                <div className="space-y-0.5">
                  <Label htmlFor="is_active">Activo</Label>
                  <p className="text-sm text-muted-foreground">Indica si el usuario está activo</p>
                </div>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch id="is_active" checked={!!field.value} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
            </div>
          </Card>

          {/* User ↔ Entidad (single) association */}
          {canSelectShelter && (
          <Card className="p-6 space-y-4 mt-6">
            <h2 className="text-xl font-semibold">Entidad del usuario</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div className="flex-1">
                <Label>Entidad</Label>
                <Combobox
                  value={selectedShelterId}
                  onChange={(v)=>{
                    setSelectedShelterId(v);
                    const opt = shelterOpts.find((o)=>o.id===v);
                    setSelectedShelterName(opt?.label || '');
                  }}
                  options={shelterOpts}
                  placeholder="Selecciona una entidad"
                />
              </div>
              <div className="flex-1">
                <Label>Función en la entidad</Label>
                <Input value={roleInShelter} onChange={(e)=>setRoleInShelter(e.target.value)} placeholder="voluntario, gestor, etc." />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={()=> setIsUnlinkDialogOpen(true)}
                  disabled={!selectedShelterId}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Quitar vínculo
                </Button>
              </div>
            </div>
          </Card>
          )}

          {/* Confirmar quitar vínculo */}
          <AlertDialog open={isUnlinkDialogOpen} onOpenChange={setIsUnlinkDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Quitar vínculo con la entidad?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. El usuario quedará sin entidad asociada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isUnlinking}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={async () => {
                    try {
                      setIsUnlinking(true);
                      if (isEdit && selectedShelterId) {
                        await usersApi.unlinkUserFromShelter(Number(id), Number(selectedShelterId));
                        await queryClient.invalidateQueries({ queryKey: ['user-shelters', id] });
                      }
                      setSelectedShelterId(undefined);
                      setSelectedShelterName('');
                      setRoleInShelter('');
                      setIsUnlinkDialogOpen(false);
                      toast({ title: 'Vínculo eliminado' });
                    } catch (e: any) {
                      toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to unlink' });
                    } finally {
                      setIsUnlinking(false);
                    }
                  }}
                >
                  {isUnlinking ? 'Eliminando...' : 'Quitar vínculo'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="flex justify-end gap-4 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/users')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
